import React from 'react'
import { LinkButton } from '@/atoms/Button'
import { GuildHighResShieldIcon } from '@/atoms/Icons/GuildHighResShieldIcon'
import { GuildIconMono } from '@/atoms/Icons/GuildIconMono'
import { MovieSpecialIcon } from '@/atoms/Icons/MovieSpecialIcon'
import { PayItForwardIcon } from '@/atoms/Icons/PayItForwardIcon'
import { PlayCircleIcon } from '@/atoms/Icons/PlayCircleIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { ResponsiveBackgroundImage } from '@/atoms/ResponsiveBackgroundImage'
import { AsH1, AsP, CaptionXS, HeadingXS, ParagraphSM } from '@/atoms/Text'
import { TitleLG } from '@/atoms/Text'
import { paths } from '@/constants'
import { ThemeContextProvider } from '@/contexts/ThemeContext'
import { VideoModal } from '@/molecules/VideoModal'
import { useGuildMember } from '@/services/GuildUserService'
import { buildLoginUrl } from '@/utils/BuildLoginUrl'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import { GuildHowDoesItWork } from '../GuildJoinView/GuildHowDoesItWork'
import { GuildBodySection } from './GuildBodySection'
import { GuildExplainerLayout } from './GuildExplainerLayout'
import { ScrollingTitles } from './GuildScrollingHero/GuildScrollingHero'
import { useGuildSignupContext } from './GuildSignupContext'

interface GuildGiftViewProps {
  hasUser: boolean
}

export function GuildGiftView({ hasUser }: GuildGiftViewProps) {
  const { guildExplainerLayout } = useGuildSignupContext()

  return (
    <main className="h-full w-full se:overflow-x-hidden">
      <ThemeContextProvider isDarkMode>
        <Hero />
        <WhyJoin />
        {guildExplainerLayout && <GuildExplainerLayout guildExplainerLayout={guildExplainerLayout} hasUser={hasUser} />}
        <ThemeContextProvider isDarkMode>
          <GuildHowDoesItWork hasUser={hasUser} />
        </ThemeContextProvider>
        <GuildCeoExplainer />
        <GuildBodySection hasUser={hasUser} />
      </ThemeContextProvider>
    </main>
  )
}

interface Reason {
  icon: React.ReactNode
  title: string
  description: string
  ctaLabel: string
}

function WhyJoin() {
  const { t } = useTranslate('guild')
  const reasons: Reason[] = [
    {
      icon: <GuildIconMono color="white" size={32} />,
      title: t('enjoyFullAccess', 'Enjoy Full Access'),
      description: t(
        'streamEntireLibrary',
        'Stream our entire library and get exclusive early access to stream films and shows in the Angel Studios app.',
      ),
      ctaLabel: t('discoverNewFavorites', 'Discover new favorites'),
    },
    {
      icon: <PayItForwardIcon color="white" size={32} />,
      title: t('watchOnAnyDevice', 'Watch On Any Device'),
      description: t(
        'enjoyYourFavoriteShows',
        'Enjoy your favorite Angel Studios shows and movies from your TV, on Angel.com, or via the mobile app.',
      ),
      ctaLabel: t('joinToShare', 'Join to share'),
    },
    {
      icon: <MovieSpecialIcon color="white" size={32} />,
      title: t('changeTheWorld', 'Change the World'),
      description: t(
        'guildOfPeopleLikeYou',
        'Join a Guild of people like you who are funding, supporting, and flooding the entertainment industry with content that amplifies light.',
      ),
      ctaLabel: t('joinToVote', 'Join to vote'),
    },
    {
      icon: <TicketIcon color="white" size={32} />,
      title: t('chooseWhatsNext', "Choose What's Next"),
      description: t(
        'becomeNewestHollywoodExecutive',
        'Become the newest Hollywood executive, and green light the titles we present on the big screen and beyond.',
      ),
      ctaLabel: t('joinToClaimYourTickets', 'Join to claim your tickets'),
    },
  ]
  return (
    <div className="relative pb-16">
      <Arc />
      <div className="mx-auto flex max-w-[1600px] flex-col p-4 text-white xl:pt-16">
        <h2 className="py-4 text-lg font-semibold">{t('whyJoinTheAngelGuild', 'Why Join The Angel Guild')}</h2>
        <ul className="grid gap-4 lg:grid-cols-4">
          {reasons.map((reason) => (
            <li key={reason.title} className="h-full">
              <ReasonCard reason={reason} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function Arc() {
  return (
    <svg
      fill="#141414"
      className="absolute z-20 w-full -translate-y-[calc(100%-6px)]"
      viewBox="0 0 1535 68"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 66.5C111.5 44.6667 419.5 1 767.5 1C1115.5 1 1423.83 44.6667 1534.5 66.5"
        stroke="url(#arc-grad)"
        stroke-width="2"
      />
      <defs>
        <linearGradient id="arc-grad" x1="1" y1="1" x2="2.48042" y2="91.5479" gradientUnits="userSpaceOnUse">
          <stop stop-color="#EDD7C6" />
          <stop offset="0.138791" stop-color="#D6A177" />
          <stop offset="0.40615" stop-color="#FEDEC6" />
          <stop offset="0.622099" stop-color="#D48D59" />
          <stop offset="0.887729" stop-color="#A66141" />
          <stop offset="0.981481" stop-color="#612B12" />
        </linearGradient>
      </defs>
    </svg>
  )
}

function ReasonCard(props: { reason: Reason }) {
  return (
    <div className="flex h-full flex-col gap-8 rounded-3xl bg-white/5 p-6 text-white">
      <header className="flex flex-col gap-2">
        <div>{props.reason.icon}</div>
        <h3 className="text-lg font-semibold lg:text-xl xl:text-2xl">{props.reason.title}</h3>
      </header>
      <p className="m">{props.reason.description}</p>
    </div>
  )
}

function GuildCeoExplainer() {
  const welcomeUrl = 'https://content.angelstudios.com/guild/impact/Angel_101_Neal_Video_1_v4_16x9_2024-8-16_CJ.mp4'
  const [showModal, setShowModal] = React.useState(false)
  const { t } = useTranslate('guild')
  return (
    <>
      <section className="lg:px-auto flex flex-col gap-12 px-4 py-12 text-white md:px-8 xl:flex-row xl:items-center xl:justify-center xl:gap-[134px] xl:px-16 3xl:gap-[200px]">
        <div>
          <TitleLG
            weight="bold"
            className="md:photon-heading-xs lg:photon-heading-sm xl:photon-heading-md text-pretty text-left xl:max-w-[418px] 2xl:max-w-[480px] 3xl:max-w-[542px]"
          >
            {t(
              'ourMissionAmplifyLightCeoExplainer',
              'At Angel Studios, our mission is to amplify light—but what exactly does that mean? Our CEO, Neal Harmon, explains our definition and how you can help us shine a light for the world to see',
            )}
            .
          </TitleLG>
        </div>
        <div className="flex justify-center xl:justify-start">
          <div className="relative max-w-[650px] cursor-pointer" onClick={() => setShowModal(true)}>
            <Image
              src={getCloudinaryImageUrl({
                path: '/v1727199971/angel-studios/guild/impact/Welcome_to_Angel.webp',
                transforms: 'q_auto:best',
              })}
              alt={t('nealWelcomeAngel', 'Neal Welcome to Angel')}
              height={601}
              width={1791}
              className="pointer-events-none rounded-3xl lg:min-h-[289px] lg:min-w-[538px]"
            />
            <PlayCircleIcon className="absolute left-1/2 top-1/2 z-10 h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full bg-core-gray-950/70" />
          </div>
        </div>
      </section>
      <VideoModal
        showModal={showModal}
        videoUrl={welcomeUrl}
        handleClose={() => setShowModal(false)}
        showButton={false}
        autoplay
      />
    </>
  )
}

function Hero() {
  return (
    <section className="relative h-[calc(100dvh-100px)] overflow-hidden">
      <link
        rel="preload"
        href="https://images.angelstudios.com/image/upload/v1731967123/angel-studios/guild/css_sprites.png"
        as="image"
      ></link>
      <ResponsiveBackgroundImage
        src="https://images.angelstudios.com/image/upload/v1715191570/angel-studios/23d15d7e3a4d9fdc60ac8a81bb38a630.webp"
        backgroundPosition="bg-left-bottom"
        className="pointer-events-none touch-none select-none"
      />
      <HeroContent />
      <ScrollingTitles className="top absolute right-0 flex h-1/2 w-full justify-center gap-x-4 opacity-70 md:h-3/4 lg:my-0 lg:h-full lg:w-1/2 lg:justify-center lg:opacity-100" />
      <div
        className="h-[250px] w-full border border-oxide-300"
        style={{ background: 'linear-gradient(180deg, rgba(20, 20, 20, 0.00) 0%, #222222 100%)' }}
      />
    </section>
  )
}

function HeroContent() {
  const { t } = useTranslate('guild')
  const { isGuildMember } = useGuildMember()
  const track = useSafeTrack()

  return (
    <div className="absolute inset-0 z-20 flex flex-col justify-end lg:max-w-[600px] lg:justify-center xl:left-[15dvw] xl:top-1/2 xl:-translate-y-1/2">
      <div className="flex flex-col gap-6 px-4 pb-20">
        <div className="flex w-full justify-center md:justify-start">
          <GuildHighResShieldIcon size={48} />
        </div>
        <HeadingXS
          as={AsH1}
          weight="bold"
          className="se:photon-title-lg md:photon-heading-lg w-full text-pretty bg-transparent text-center md:text-left xl:text-5xl xl:leading-[54px]"
          color="white"
        >
          {t('giftTheGuildGuildMembership', 'Give the Gift of a Guild Membership.')}
        </HeadingXS>
        <ParagraphSM
          as={AsP}
          className="w-full text-pretty rounded-md text-center text-shadow-01 backdrop-blur-sm md:text-left lg:max-w-[450px]"
          weight="medium"
          color="core-gray-200"
        >
          {isGuildMember
            ? t(
                'joinTheMovementWithAFriendGuild',
                'Support inspiring shows & films by gifting a year of a Guild membership to a loved one this Christmas! ',
              )
            : t('joinTheMovementWithAFriend', 'Join the movement to support inspiring shows & films with a friend.')}
        </ParagraphSM>
        <LinkButton
          variant="white"
          href={paths.external.giftGuild}
          className="md:self-start"
          onClick={() => {
            track('Guild View Gift Options', { isGuildMember })
          }}
        >
          {t('viewGiftOptions', 'View Gift Options Here')}
        </LinkButton>
        {!isGuildMember && (
          <CaptionXS
            as={AsP}
            className="w-full text-pretty rounded-md text-center backdrop-blur-sm md:text-left"
            color="core-gray-200"
          >
            {t('alreadyAnAngelMember', 'Already an Angel member?')}{' '}
            <InternalLink href={buildLoginUrl(paths.guild.gift)} className="text-xs text-oxide">
              {t('login', 'Log In')}
            </InternalLink>
          </CaptionXS>
        )}
      </div>
    </div>
  )
}
